import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import AppDialog from "@/components/common/AppDialog";
import { showToast } from "@/lib/utils/toast";
import SurveyResponseList from "./SurveyResponseList";
import { useSurveysStore, useSurveysUiStore } from "../hooks";
import { surveysService } from "../services";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const ResponsesTab = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const {
    responses,
    responsesLoading,
    responsesError,
    responsesPage,
    responsesPageSize,
    responsesTotalPages,
    setResponsesPage,
    setResponsesPageSize,
    loadResponses,
  } = useSurveysStore();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const surveysUiStore = useSurveysUiStore();
  const {
    responsesDeleteTarget,
    responsesDeleteLoading,
    openResponsesDelete,
    closeResponsesDelete,
    setResponsesDeleteLoading,
  } = surveysUiStore;

  useEffect(() => {
    loadResponses({ page: responsesPage, limit: responsesPageSize });
  }, [loadResponses, responsesPage, responsesPageSize]);

  const handleDelete = async () => {
    if (!responsesDeleteTarget?._id) return;
    setResponsesDeleteLoading(true);
    try {
      const { success } = await surveysService.deleteSurveyResponse(
        responsesDeleteTarget._id,
      );
      if (!success) {
        throw new Error(t("Unable to delete response."));
      }
      showToast.success(t("Response deleted."));
      closeResponsesDelete();
      loadResponses({
        page: responsesPage,
        limit: responsesPageSize,
      });
    } catch (error) {
      showToast.error(error?.message || t("Unable to delete response."));
    } finally {
      setResponsesDeleteLoading(false);
    }
  };

  if (responsesError) {
    return <p className="text-sm text-destructive">{responsesError}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {t("Responses")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("View submitted survey responses and scores.")}
          </p>
        </div>
        <Can resource="surveys" action="view" userPermissions={permissions}>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              loadResponses({
                page: responsesPage,
                limit: responsesPageSize,
              })
            }
          >
            {t("Refresh")}
          </Button>
        </Can>
      </div>

      <SurveyResponseList
        responses={responses}
        loading={responsesLoading}
        page={responsesPage}
        totalPages={responsesTotalPages}
        pageSize={responsesPageSize}
        onPageChange={setResponsesPage}
        onPageSizeChange={(size) => {
          setResponsesPageSize(size);
          setResponsesPage(1);
        }}
        onView={(response) =>
          navigate(
            PATHS.SURVEY_RESPONSE_DETAIL.replace(
              ":responseId",
              response?._id || response?.id || "",
            ),
          )
        }
        onDelete={(response) => openResponsesDelete(response)}
      />

      <AppDialog
        open={Boolean(responsesDeleteTarget)}
        onOpenChange={(open) => {
          if (!open) closeResponsesDelete();
        }}
        title={t("Delete response")}
        description={t("This will permanently delete the response.")}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeResponsesDelete}>
              {t("Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={responsesDeleteLoading}
            >
              {responsesDeleteLoading ? t("Deleting...") : t("Delete")}
            </Button>
          </div>
        }
      />
    </div>
  );
});

export default ResponsesTab;
